/**
 * DirectApprovalModal — used when a barrister approves or declines a matter
 * directly, bypassing the formal conference requirement.
 *
 * Reflects real chambers practice: approval may happen via phone call,
 * in-person discussion, or direct barrister knowledge — before any
 * scheduled conference takes place.
 *
 * The approval note is MANDATORY. It becomes a permanent part of the
 * matter's audit trail and timeline.
 */

import { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  Warning,
  X,
  Scales,
} from '@phosphor-icons/react';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  mode: 'approve' | 'decline';
  matterName: string;
  onConfirm: (note: string) => Promise<void>;
  onClose: () => void;
}

const APPROVE_PLACEHOLDERS = [
  'Approved after direct discussion with clerk.',
  'Proceeding without formal conference — matter reviewed independently.',
  'Instructions accepted following telephone discussion with instructing solicitor.',
  'Matter accepted after review of papers and clerk recommendation.',
  'Conference not required — sufficient information obtained directly.',
];

const DECLINE_PLACEHOLDERS = [
  'Diary clash — committed to existing trial during the relevant period.',
  'Conflict of interest identified following further review.',
  'Outside current practice area — insufficient expertise to accept.',
  'Insufficient merits to justify acceptance at this stage.',
  'Unable to accept — capacity constraints for the foreseeable period.',
];

export function DirectApprovalModal({ mode, matterName, onConfirm, onClose }: Props) {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isApprove = mode === 'approve';
  const placeholder = isApprove
    ? APPROVE_PLACEHOLDERS[Math.floor(Math.random() * APPROVE_PLACEHOLDERS.length)]
    : DECLINE_PLACEHOLDERS[Math.floor(Math.random() * DECLINE_PLACEHOLDERS.length)];

  const trimmed = note.trim();
  const canSubmit = trimmed.length >= 10 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(trimmed);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Could not complete action.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <ScrollLock />
      <div className="my-auto w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">

        {/* Header */}
        <div className={`flex items-start justify-between px-6 py-5 ${isApprove ? 'bg-emerald-50 dark:bg-emerald-500/10 border-b border-emerald-100' : 'bg-red-50 dark:bg-red-500/10 border-b border-red-100'}`}>
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isApprove ? 'bg-emerald-600' : 'bg-red-600'}`}>
              {isApprove
                ? <CheckCircle className="h-5 w-5 text-white" weight="bold" />
                : <XCircle className="h-5 w-5 text-white" weight="bold" />}
            </div>
            <div>
              <h2 className={`text-base font-bold ${isApprove ? 'text-emerald-900 dark:text-emerald-200' : 'text-red-900 dark:text-red-200'}`}>
                {isApprove ? 'Approve Matter Directly' : 'Decline Matter'}
              </h2>
              <p className={`text-xs ${isApprove ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
                {matterName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-muted/60"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Context explanation */}
        <div className="border-b border-border bg-muted/30 px-6 py-4">
          <div className="flex items-start gap-2.5">
            <Scales className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" weight="duotone" />
            <div className="text-[12.5px] leading-relaxed text-muted-foreground">
              {isApprove ? (
                <>
                  You are approving this matter <strong className="text-foreground">without a formal conference</strong>.
                  This reflects real chambers practice — approval may follow a phone call, direct clerk
                  discussion, or your prior knowledge of the matter.
                  <br /><br />
                  The matter will immediately become <strong className="text-foreground">Active</strong>.
                  Any pending conference will be marked as no longer required.
                </>
              ) : (
                <>
                  You are declining this matter <strong className="text-foreground">without a formal conference</strong>.
                  Any pending conference will be cancelled automatically.
                  The clerk will be notified and may re-allocate to another barrister.
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mandatory note */}
        <div className="px-6 py-5 space-y-3">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.08em] text-foreground mb-1.5">
              {isApprove ? 'Approval note' : 'Decline reason'}
              <span className="ml-1.5 rounded-full bg-red-100 dark:bg-red-500/15 px-1.5 py-0.5 text-[9px] font-bold text-red-700 dark:text-red-300">REQUIRED</span>
            </label>
            <ExpandableTextarea
              label={isApprove ? 'Approval note' : 'Decline reason'}
              value={note}
              onChange={setNote}
              placeholder={`e.g. "${placeholder}"`}
              rows={4}
              disabled={submitting}
            />
            <div className="mt-1.5 flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                This note is recorded permanently in the matter timeline and audit log.
              </p>
              <span className={`text-[10px] ${trimmed.length < 10 ? 'text-muted-foreground/60' : 'text-emerald-600 dark:text-emerald-400'}`}>
                {trimmed.length}/1000
              </span>
            </div>
          </div>

          {trimmed.length > 0 && trimmed.length < 10 && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2">
              <Warning className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" weight="fill" />
              <p className="text-xs text-amber-800 dark:text-amber-300">Please provide at least a brief note ({10 - trimmed.length} more characters).</p>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-3 py-2 text-xs text-red-800 dark:text-red-300">
              {error}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 border-t border-border bg-muted/20 px-6 py-4">
          <button
            onClick={onClose}
            disabled={submitting}
            className="rounded-xl border border-border bg-card px-5 py-2 text-sm font-medium text-muted-foreground transition hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`flex items-center gap-2 rounded-xl px-6 py-2 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-40 ${
              isApprove
                ? 'bg-emerald-600 hover:bg-emerald-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {submitting ? (
              <span className="animate-pulse">{isApprove ? 'Approving…' : 'Declining…'}</span>
            ) : (
              <>
                {isApprove
                  ? <CheckCircle className="h-4 w-4" weight="bold" />
                  : <XCircle className="h-4 w-4" weight="bold" />}
                {isApprove ? 'Approve Matter' : 'Decline Matter'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
